import type { ReactNode } from 'react';

/**
 * Borderless field rows: a 260px label column beside a value column, separated by hairline
 * dividers, with no card chrome around them. `FieldRows` is the list, `FieldRow` one row, and
 * `FieldValue` the plain read-only text that sits in a row's value column.
 */
export function FieldRows({ children }: { children: ReactNode }) {
  return <div className="flex flex-col divide-y divide-line-subtle">{children}</div>;
}

interface FieldRowProps {
  label: string;
  description?: string;
  children: ReactNode;
}

export function FieldRow({ label, description, children }: FieldRowProps) {
  return (
    <div className="grid grid-cols-[260px_1fr] items-center gap-6 py-4.5">
      <div className="flex flex-col gap-0.5">
        <span className="text-sm font-medium text-fg">{label}</span>
        {description && <span className="text-xs leading-4.25 text-fg-tertiary">{description}</span>}
      </div>
      <div className="flex min-w-0 items-center gap-2.5">{children}</div>
    </div>
  );
}

/** Plain read-only value text used inside a field row's value column. */
export function FieldValue({ children }: { children: ReactNode }) {
  return <span className="flex-1 truncate text-sm text-fg-secondary">{children}</span>;
}
